/**
 * Script Executor
 * Executes JavaScript with trust level enforcement and sandboxing
 * ⚠️ SECURITY: Critical trust level validation, rate limiting, loop detection
 */

import {
  JavaScriptBlock,
  JavaScriptContext,
  ControlScripting,
} from '../scripting/scripting.types';
import { TrustLevel } from '../security/security.types';
import { RateLimiter, RateLimitConfig } from '../security/RateLimiter';
import { InfiniteLoopDetector, LoopDetectionConfig } from '../security/InfiniteLoopDetector';
import { IAuditStorage, InMemoryAuditStorage } from '../security/AuditPersistence';
import { ApprovalWorkflow, IApprovalStore } from '../security/ApprovalWorkflow';

/**
 * Result of script execution
 */
export interface ScriptExecutionResult {
  success: boolean;
  scriptId: string;
  result?: any;
  error?: string;
  executionTime: number;
  blocked?: boolean;
  blockReason?: string;
}

/**
 * Audit log entry for script execution
 */
export interface ScriptAuditEntry {
  timestamp: number;
  scriptId: string;
  scriptName: string;
  trustLevel: TrustLevel;
  allowed: boolean;
  reason: string;
  executionTime?: number;
  error?: string;
}

/**
 * Script executor with trust level enforcement
 * Prevents execution of untrusted scripts
 * Includes rate limiting, infinite loop detection, audit persistence, and approval workflow
 */
export class ScriptExecutor {
  private auditLog: ScriptAuditEntry[] = [];
  private executionTimeout: number = 5000; // 5 seconds default
  private rateLimiter: RateLimiter;
  private loopDetector: InfiniteLoopDetector;
  private auditStorage: IAuditStorage;
  private approvalWorkflow: ApprovalWorkflow;
  private enableRateLimit: boolean = true;
  private enableLoopDetection: boolean = true;

  constructor(
    executionTimeout?: number,
    options?: {
      rateLimitConfig?: RateLimitConfig;
      loopDetectionConfig?: LoopDetectionConfig;
      auditStorage?: IAuditStorage;
      approvalStore?: IApprovalStore;
      enableRateLimit?: boolean;
      enableLoopDetection?: boolean;
    }
  ) {
    if (executionTimeout) {
      this.executionTimeout = executionTimeout;
    }

    this.rateLimiter = new RateLimiter(options?.rateLimitConfig);
    this.loopDetector = new InfiniteLoopDetector(options?.loopDetectionConfig);
    this.auditStorage = options?.auditStorage ?? new InMemoryAuditStorage();
    this.approvalWorkflow = new ApprovalWorkflow(options?.approvalStore);
    this.enableRateLimit = options?.enableRateLimit ?? true;
    this.enableLoopDetection = options?.enableLoopDetection ?? true;
  }

  /**
   * Execute a script with trust level validation
   *
   * Trust Level Rules:
   * - Level 0 (Trusted): Scripts execute normally
   * - Level 1 (Untrusted): Scripts are blocked
   * - Level 2 (Explicit): Scripts require approval
   */
  async executeScript(
    script: JavaScriptBlock,
    context: JavaScriptContext,
    effectiveTrustLevel: TrustLevel
  ): Promise<ScriptExecutionResult> {
    const startTime = Date.now();

    // Step 1: Check if script is enabled
    if (!script.enabled) {
      const auditEntry: ScriptAuditEntry = {
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: false,
        reason: 'Script is disabled',
      };

      this.logAudit(auditEntry);
      await this.auditStorage.save(auditEntry);

      return {
        success: false,
        scriptId: script.scriptId,
        blocked: true,
        blockReason: 'Script is disabled',
        executionTime: Date.now() - startTime,
      };
    }

    // Step 2: Check rate limit
    if (this.enableRateLimit) {
      const rateLimitStatus = this.rateLimiter.checkLimit(context.controlId);
      if (!rateLimitStatus.allowed) {
        const auditEntry: ScriptAuditEntry = {
          timestamp: startTime,
          scriptId: script.scriptId,
          scriptName: script.name,
          trustLevel: effectiveTrustLevel,
          allowed: false,
          reason: rateLimitStatus.reason || 'Rate limit exceeded',
        };

        this.logAudit(auditEntry);
        await this.auditStorage.save(auditEntry);

        return {
          success: false,
          scriptId: script.scriptId,
          blocked: true,
          blockReason: rateLimitStatus.reason || 'Rate limit exceeded',
          executionTime: Date.now() - startTime,
        };
      }
    }

    // Step 3: Enforce trust level
    if (effectiveTrustLevel === 1) {
      // Level 1 (Untrusted): Block all scripts
      const auditEntry: ScriptAuditEntry = {
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: false,
        reason: 'Trust level 1 (Untrusted) blocks script execution',
      };

      this.logAudit(auditEntry);
      await this.auditStorage.save(auditEntry);

      return {
        success: false,
        scriptId: script.scriptId,
        blocked: true,
        blockReason: 'Trust level 1 (Untrusted) blocks script execution',
        executionTime: Date.now() - startTime,
      };
    }

    if (effectiveTrustLevel === 2) {
      // Level 2 (Explicit): Requires user approval
      const isApproved = await this.approvalWorkflow.canExecute(script.scriptId);
      if (!isApproved) {
        const auditEntry: ScriptAuditEntry = {
          timestamp: startTime,
          scriptId: script.scriptId,
          scriptName: script.name,
          trustLevel: effectiveTrustLevel,
          allowed: false,
          reason: 'Trust level 2 (Explicit) requires user approval',
        };

        this.logAudit(auditEntry);
        await this.auditStorage.save(auditEntry);

        return {
          success: false,
          scriptId: script.scriptId,
          blocked: true,
          blockReason: 'Trust level 2 (Explicit) requires user approval',
          executionTime: Date.now() - startTime,
        };
      }
    }

    // Step 4: Check for infinite loops
    if (this.enableLoopDetection) {
      if (this.loopDetector.hasInfiniteSuspicion(script.code)) {
        const auditEntry: ScriptAuditEntry = {
          timestamp: startTime,
          scriptId: script.scriptId,
          scriptName: script.name,
          trustLevel: effectiveTrustLevel,
          allowed: false,
          reason: 'Script contains suspected infinite loop pattern',
        };

        this.logAudit(auditEntry);
        await this.auditStorage.save(auditEntry);

        return {
          success: false,
          scriptId: script.scriptId,
          error: 'Script contains suspected infinite loop pattern',
          executionTime: Date.now() - startTime,
        };
      }
    }

    // Step 5: Validate syntax
    let instrumentedCode = script.code;
    try {
      new Function(script.code); // Syntax check only

      // Instrument code for loop detection
      if (this.enableLoopDetection) {
        instrumentedCode = this.loopDetector.instrumentCode(script.code);
      }
    } catch (e) {
      const error = String(e);
      const auditEntry: ScriptAuditEntry = {
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: false,
        reason: 'Invalid JavaScript syntax',
        error,
      };

      this.logAudit(auditEntry);
      await this.auditStorage.save(auditEntry);

      return {
        success: false,
        scriptId: script.scriptId,
        error: `Invalid syntax: ${error}`,
        executionTime: Date.now() - startTime,
      };
    }

    // Step 6: Execute with timeout
    try {
      const result = await Promise.race([
        this.executeInContext(instrumentedCode, context),
        this.createTimeout(),
      ]);

      await this.auditStorage.save({
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: true,
        reason: 'Script executed successfully',
        executionTime: Date.now() - startTime,
      });

      this.logAudit({
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: true,
        reason: 'Script executed successfully',
        executionTime: Date.now() - startTime,
      });

      return {
        success: true,
        scriptId: script.scriptId,
        result,
        executionTime: Date.now() - startTime,
      };
    } catch (e) {
      const error = String(e);
      const auditEntry: ScriptAuditEntry = {
        timestamp: startTime,
        scriptId: script.scriptId,
        scriptName: script.name,
        trustLevel: effectiveTrustLevel,
        allowed: true, // Execution was allowed, but failed
        reason: 'Script execution failed',
        error,
      };

      await this.auditStorage.save(auditEntry);
      this.logAudit(auditEntry);

      return {
        success: false,
        scriptId: script.scriptId,
        error: String(e),
        executionTime: Date.now() - startTime,
      };
    }
  }

  /**
   * Execute script in isolated context using Web Worker
   * Real sandbox isolation with no access to main thread globals
   */
  private async executeInContext(
    code: string,
    context: JavaScriptContext
  ): Promise<any> {
    // Check if Web Workers are available (browser environment)
    if (typeof Worker !== 'undefined') {
      return this.executeInWorker(code, context);
    }

    // Fallback to function scope isolation for Node.js environment
    return this.executeInFunctionScope(code, context);
  }

  /**
   * Execute script in Web Worker (true sandbox)
   */
  private async executeInWorker(code: string, context: JavaScriptContext): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        // Create worker code that runs the user script in isolation
        const workerCode = `
          self.onmessage = function(event) {
            const { code, context } = event.data;
            try {
              const controlId = context.controlId;
              const controlValue = context.controlValue;
              const controlProps = context.controlProps;
              const formData = context.formData;

              // Safe console object
              const console = {
                log: function(...args) { self.postMessage({ type: 'log', args }); },
                info: function(...args) { self.postMessage({ type: 'info', args }); },
                warn: function(...args) { self.postMessage({ type: 'warn', args }); },
                error: function(...args) { self.postMessage({ type: 'error', args }); },
                debug: function(...args) { self.postMessage({ type: 'debug', args }); },
              };

              // Create function and execute
              const fn = new Function('controlId', 'controlValue', 'controlProps', 'formData', 'console', code);
              const result = fn(controlId, controlValue, controlProps, formData, console);

              self.postMessage({ type: 'result', data: result });
            } catch (error) {
              self.postMessage({ type: 'error', error: error.message });
            }
          };
        `;

        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const worker = new Worker(URL.createObjectURL(blob));

        const timeout = setTimeout(() => {
          worker.terminate();
          reject(new Error(`Script execution timeout (${this.executionTimeout}ms)`));
        }, this.executionTimeout);

        worker.onmessage = (event) => {
          clearTimeout(timeout);
          worker.terminate();

          if (event.data.type === 'result') {
            resolve(event.data.data);
          } else if (event.data.type === 'error') {
            reject(new Error(event.data.error));
          }
        };

        worker.onerror = (error) => {
          clearTimeout(timeout);
          worker.terminate();
          reject(error);
        };

        // Send code and context to worker
        worker.postMessage({ code, context });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Execute script in function scope with strict isolation (Node.js fallback)
   * Uses strict mode and frozen objects to prevent access to globals
   */
  private async executeInFunctionScope(code: string, context: JavaScriptContext): Promise<any> {
    'use strict';

    // Create safe context with limited globals
    const sandbox = {
      // Safe APIs
      console: {
        log: console.log.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        debug: console.debug.bind(console),
      },
      JSON: JSON,
      Math: Math,
      Number: Number,
      String: String,
      Boolean: Boolean,
      Array: Array,
      // Restricted Object: Only allow JSON operations, not prototype access
      Object: {
        keys: Object.keys,
        values: Object.values,
        entries: Object.entries,
        assign: Object.assign,
        freeze: Object.freeze,
        seal: Object.seal,
      } as any,
      Date: Date,
      RegExp: RegExp,
      Set: Set,
      Map: Map,
      setTimeout: undefined, // Prevent async tricks
      setInterval: undefined,
      setImmediate: undefined,

      // Control data - frozen
      controlId: context.controlId,
      controlValue: context.controlValue,
      controlProps: Object.freeze({ ...context.controlProps }),
      formData: Object.freeze({ ...context.formData }),

      // Deny access to dangerous globals
      eval: undefined,
      Function: undefined,
      fetch: undefined,
      XMLHttpRequest: undefined,
      WebSocket: undefined,
      Worker: undefined,
      SharedArrayBuffer: undefined,
      Proxy: undefined,
      Reflect: undefined,
      require: undefined,
      module: undefined,
      global: undefined,
      globalThis: undefined,
    };

    // Freeze sandbox to prevent any modifications
    Object.freeze(sandbox);
    Object.freeze(Object.getPrototypeOf(sandbox));

    // Create wrapped function that prevents access to outer scope
    // Using Function constructor with 'use strict' is the only way in Node.js
    const fn = new Function('sandbox', `'use strict'; with(sandbox) { return (${code}); }`);
    return fn(sandbox);
  }

  /**
   * Create execution timeout promise
   */
  private createTimeout(): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`Script execution timeout (${this.executionTimeout}ms)`)),
        this.executionTimeout
      );
    });
  }

  /**
   * Execute multiple scripts in sequence
   */
  async executeScripts(
    scripts: ControlScripting,
    context: JavaScriptContext,
    formTrustLevel: TrustLevel = 0,
    controlTrustLevel?: TrustLevel
  ): Promise<ScriptExecutionResult[]> {
    const results: ScriptExecutionResult[] = [];

    for (const script of scripts.scripts) {
      // Skip disabled scripts
      if (!script.enabled || !scripts.enabledScripts.includes(script.scriptId)) {
        continue;
      }

      // Resolve effective trust level (lowest = most restrictive)
      const effectiveTrustLevel = this.resolveTrustLevel(
        formTrustLevel,
        controlTrustLevel,
        scripts.trustLevel
      );

      const result = await this.executeScript(script, context, effectiveTrustLevel);
      results.push(result);

      // Stop on critical errors
      if (!result.success && result.error) {
        break;
      }
    }

    return results;
  }

  /**
   * Resolve effective trust level (lowest = most restrictive)
   */
  private resolveTrustLevel(
    formLevel: TrustLevel,
    controlLevel?: TrustLevel,
    scriptLevel?: TrustLevel
  ): TrustLevel {
    const levels = [formLevel];
    if (controlLevel !== undefined) levels.push(controlLevel);
    if (scriptLevel !== undefined) levels.push(scriptLevel);

    // Return minimum (most restrictive) trust level
    return Math.min(...levels) as TrustLevel;
  }

  /**
   * Log audit entry
   */
  private logAudit(entry: ScriptAuditEntry): void {
    this.auditLog.push(entry);

    // Keep only last 1000 entries
    if (this.auditLog.length > 1000) {
      this.auditLog = this.auditLog.slice(-1000);
    }
  }

  /**
   * Get audit log
   */
  getAuditLog(): ScriptAuditEntry[] {
    return [...this.auditLog];
  }

  /**
   * Get audit entries for a specific script
   */
  getScriptAuditLog(scriptId: string): ScriptAuditEntry[] {
    return this.auditLog.filter((entry) => entry.scriptId === scriptId);
  }

  /**
   * Clear audit log
   */
  clearAuditLog(): void {
    this.auditLog = [];
  }

  /**
   * Get statistics about script executions
   */
  getStatistics(): {
    totalExecutions: number;
    successful: number;
    failed: number;
    blocked: number;
    averageExecutionTime: number;
  } {
    const successful = this.auditLog.filter((e) => e.allowed && !e.error).length;
    const failed = this.auditLog.filter((e) => e.allowed && e.error).length;
    const blocked = this.auditLog.filter((e) => !e.allowed).length;
    const totalExecutions = this.auditLog.length;

    const executionTimes = this.auditLog
      .filter((e) => e.executionTime)
      .map((e) => e.executionTime!);
    const averageExecutionTime =
      executionTimes.length > 0
        ? executionTimes.reduce((a, b) => a + b, 0) / executionTimes.length
        : 0;

    return {
      totalExecutions,
      successful,
      failed,
      blocked,
      averageExecutionTime,
    };
  }

  /**
   * Get approval workflow (for trustLevel 2 management)
   */
  getApprovalWorkflow(): ApprovalWorkflow {
    return this.approvalWorkflow;
  }

  /**
   * Get rate limiter (for DOS protection)
   */
  getRateLimiter(): RateLimiter {
    return this.rateLimiter;
  }

  /**
   * Get loop detector (for infinite loop prevention)
   */
  getLoopDetector(): InfiniteLoopDetector {
    return this.loopDetector;
  }

  /**
   * Get audit storage (for compliance and debugging)
   */
  getAuditStorage(): IAuditStorage {
    return this.auditStorage;
  }

  /**
   * Query persistent audit log
   */
  async queryAuditLog(filter?: {
    startTime?: number;
    endTime?: number;
    scriptId?: string;
    allowedOnly?: boolean;
    blockedOnly?: boolean;
    limit?: number;
  }): Promise<ScriptAuditEntry[]> {
    return this.auditStorage.query(filter);
  }
}
